import React, { createContext, useState, useEffect } from "react";
import finacraft_api, { setAccessToken } from "../api_adapters/finacraft_api";

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user_data, setUserData] = useState({});
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);

  // helper to fetch user info
  const fetchUserInfo = async () => {
    try {
      const res = await finacraft_api.get("/core_api/clients/me/");
      setUserData(res.data);
    } catch (err) {
      console.error("Failed to fetch user info:", err);
      setUserData({});
    }
  };

  const fetchOnboardingStatus = async () => {
    try {
      const res = await finacraft_api.get("/core_api/clients/onboarding-status/");
      setOnboardingCompleted(res.data.onboarding_completed);
    } catch (err) {
      console.error("Failed to fetch onboarding status:", err);
      return setOnboardingCompleted(false);
    }
  };

  useEffect(() => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      setLoading(false);
      setIsAuthenticated(false);
      setUserData({});
      return;
    }

    (async () => {
      try {
        const res = await finacraft_api.post("/auth/token/refresh/", {
          refresh: refreshToken,
        });
        localStorage.setItem("access_token", res.data.access);
        setAccessToken(res.data.access);
        setIsAuthenticated(true);

        // fetch user profile after refresh succeeds
        await fetchUserInfo();
        await fetchOnboardingStatus();
      } catch (err) {
        console.warn("Token refresh failed", err);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        setIsAuthenticated(false);
        setUserData({});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loginWithGoogle = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      setError("No credential returned from Google");
      return;
    }
    try {
      const res = await finacraft_api.post("/auth/google/", { id_token: idToken });
      localStorage.setItem("access_token", res.data.access);
      localStorage.setItem("refresh_token", res.data.refresh);
      setAccessToken(res.data.access);
      setIsAuthenticated(true);

      // fetch user profile after login succeeds
      await fetchUserInfo();
      await fetchOnboardingStatus();
      setError(null);
      return { success: true };
    } catch (err) {
      console.error("Backend authentication failed", err);
      setError("Login failed: Not invited or backend error");
      return { success: false, error: err };
    }
  };

  const logout = async () => {
    try {
      await finacraft_api.post("/auth/logout/");
    } catch (err) {
      console.warn("Logout request failed:", err);
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setAccessToken(null);
    setIsAuthenticated(false);
    setUserData({});
    window.location.href = "/login";
    
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        loading,
        error,
        user_data,
        onboardingCompleted,
        loginWithGoogle,
        logout,
        setError,
        setUserData,
        fetchOnboardingStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
